'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  ArrowRight, 
  Sparkles, 
  TrendingUp, 
  Award,
  ShieldCheck,
  Leaf,
  ChefHat,
  Users,
  Star,
  Clock,
  MapPin,
  Map,
  Loader
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { GangwonMap } from '@/components/GangwonMap';
import { TrendingRecipeCarousel } from '@/components/TrendingRecipeCarousel';
import { ProductAnalysis } from '@/components/ProductAnalysis';

const trendingRecipes = [
  { id: 1, title: '평창 감자범벅 (감자붕생이)', category: '전통요리', time: '25분', difficulty: '쉬움', image: '🥔', description: '강원도 향토음식, 옛날 간식' },
  { id: 2, title: '정선 메밀전병', category: '전통요리', time: '30분', difficulty: '보통', image: '🥞', description: '강원도 대표 향토음식' },
  { id: 3, title: '춘천식 닭갈비', category: '구이요리', time: '40분', difficulty: '보통', image: '🍗', description: '춘천 대표 음식' },
  { id: 4, title: '인제 황태구이', category: '생선요리', time: '25분', difficulty: '쉬움', image: '🐟', description: '깊은 감칠맛의 건어물' },
  { id: 5, title: '곰취나물 들기름볶음', category: '산나물', time: '20분', difficulty: '쉬움', image: '🥬', description: '강원도 산나물의 정수' },
  { id: 6, title: '홍천 찰옥수수 범벅', category: '전통요리', time: '30분', difficulty: '쉬움', image: '🌽', description: '고소한 옥수수 요리' },
  { id: 7, title: '양양 황태해장국', category: '찌개/국', time: '45분', difficulty: '보통', image: '🍲', description: '시원한 해장국' },
  { id: 8, title: '평창 감자옹심이', category: '전통요리', time: '60분', difficulty: '어려움', image: '🥟', description: '쫄깃한 감자 수제비' },
  { id: 9, title: '횡성 한우불고기', category: '구이요리', time: '35분', difficulty: '보통', image: '🥩', description: '프리미엄 한우 요리' },
  { id: 10, title: '속초 아바이순대', category: '전통요리', time: '90분', difficulty: '어려움', image: '🌭', description: '속초 향토 별미' },
  { id: 11, title: '강릉 초당순두부찌개', category: '찌개/국', time: '25분', difficulty: '쉬움', image: '🥛', description: '부드러운 순두부 요리' },
  { id: 12, title: '정선 곤드레밥', category: '전통요리', time: '55분', difficulty: '보통', image: '🍚', description: '영양만점 산나물밥' },
  { id: 13, title: '취나물 무침', category: '산나물', time: '15분', difficulty: '쉬움', image: '🌿', description: '봄나물의 대표주자' },
  { id: 14, title: '철원 메밀막국수', category: '면요리', time: '20분', difficulty: '쉬움', image: '🍜', description: '시원한 메밀국수' },
  { id: 15, title: '영월 감자부침개', category: '전통요리', time: '25분', difficulty: '쉬움', image: '🥔', description: '바삭한 감자전' },
  { id: 16, title: '태백 곰취냉면', category: '면요리', time: '30분', difficulty: '보통', image: '🍝', description: '산나물이 들어간 특별한 냉면' },
  { id: 17, title: '고성 명태조림', category: '생선요리', time: '35분', difficulty: '보통', image: '🐟', description: '동해안 대표 생선 요리' },
  { id: 18, title: '화천 산천어회', category: '생선요리', time: '15분', difficulty: '어려움', image: '🍣', description: '겨울 별미 민물고기' },
  { id: 19, title: '동해 대게찜', category: '해산물', time: '40분', difficulty: '보통', image: '🦀', description: '동해안 겨울 진미' },
  { id: 20, title: '원주 잣죽', category: '죽/미음', time: '45분', difficulty: '보통', image: '🥜', description: '고소하고 영양만점' }
];

const farmStories = [
  { 
    id: 1, 
    farm: '평창 행복농장', 
    title: '3대째 이어온 감자 농사 이야기', 
    location: '강원도 평창군',
    verified: true,
    rating: 4.8
  },
  { 
    id: 2, 
    farm: '횡성 청정한우', 
    title: '자연방목으로 키운 건강한 한우', 
    location: '강원도 횡성군',
    verified: true,
    rating: 4.9
  },
  { 
    id: 3, 
    farm: '속초 바다농장', 
    title: '동해 청정해역의 신선한 수산물', 
    location: '강원도 속초시',
    verified: true,
    rating: 4.7
  },
];

export default function HomePage() {
  const [seasonalProducts, setSeasonalProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<string>('');
  const [showAnalysis, setShowAnalysis] = useState(false);

  // 계절별 강원도 제철 특산물
  const seasonalProductsData = {
    spring: [ // 봄 (3-5월)
      '강릉 두릅', '정선 참두릅', '인제 참두릅', '정선 개두릅', '인제 명이나물', '영월 산마늘',
      '강릉 곰취', '동해 곰취', '양구 곰취', '인제 곰취', '영월 곰취', '태백 곰취찐빵',
      '강릉 곤드레', '정선 곤드레', '정선 취나물', '정선 어수리', 
      '인제 고로쇠수액', '삼척 딸기', '춘천 총떡'
    ],
    summer: [ // 여름 (6-8월)
      '철원 토마토', '화천 토마토', '횡성 토마토', '영월 토마토',
      '강릉 옥수수', '홍천 찰옥수수', '정선 찰옥수수', '영월 찰옥수수', '평창 옥수수', '인제 미백찰옥수수',
      '양구 멜론', '평창 멜론', '양구 아스파라거스', '인제 아스파라거스',
      '춘천 블루베리', '인제 블루베리', '치악산 복숭아', '강릉 무지개송어', '평창 송어'
    ],
    autumn: [ // 가을 (9-11월)
      '치악산 사과', '동해 사과', '양구 사과', '횡성 사과', '평창 사과', '영월 사과',
      '치악산 배', '양구 포도', '영월 포도', '강릉 곶감', '치악산 토종다래', '영월 다래',
      '철원 오대쌀', '양구 오대쌀', '홍천 쌀', '원주 토토미',
      '평창 배추', '횡성 절임배추', '영월 절임배추', '원주 조엄고구마',
      '양양 송이버섯', '원주 큰송이버섯', '평창 표고버섯', '정선 표고버섯', '영월 느타리버섯'
    ],
    winter: [ // 겨울 (12-2월)  
      '양구 시래기', '인제 황태', '양양 황태', '평창 황태', '고성 명태',
      '평창 감자', '속초 감자', '정선 감자', '영월 감자', '인제 수미감자',
      '화천 산천어', '양양 연어', '고성 대게', '삼척 대게', '동해 대게',
      '평창 메밀', '정선 메밀', '철원 메밀', '홍천 잣', '정선 잣', '화천 잣'
    ]
  };

  // 현재 계절 확인 함수
  const getCurrentSeason = () => {
    const month = new Date().getMonth() + 1; // 1-12
    if (month >= 3 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    if (month >= 9 && month <= 11) return 'autumn';
    return 'winter';
  };

  // 현재 계절에 맞는 상품들을 우선적으로, 다른 계절 상품들도 포함
  const getAllSearchQueries = () => {
    const currentSeason = getCurrentSeason();
    const currentSeasonProducts = seasonalProductsData[currentSeason];
    
    // 다른 계절 상품들도 포함 (비율 조정)
    const otherSeasons = Object.keys(seasonalProductsData).filter(s => s !== currentSeason);
    const otherSeasonProducts = otherSeasons.flatMap(season => 
      seasonalProductsData[season as keyof typeof seasonalProductsData].slice(0, 5) // 각 계절에서 5개씩
    );
    
    // 연중 상품들 추가
    const yearRoundProducts = [
      '춘천 닭갈비', '춘천 막국수', '강릉 막국수', '춘천 감자빵', '강릉 짬뽕빵', '강릉 초당순두부',
      '속초 아바이순대', '강릉 오징어순대', '속초 오징어순대', '속초 닭강정',
      '횡성 한우', '치악산 한우', '대관령 한우', '홍천 한우', '영월 한우',
      '안흥찐빵', '태백 곰취냉면', '정선 곤드레밥', '정선 메밀전병', '정선 올챙이국수'
    ];
    
    // 현재 계절 상품 70%, 다른 계절 20%, 연중 상품 10% 비율로 조합
    return [
      ...currentSeasonProducts,
      ...currentSeasonProducts.slice(0, Math.floor(currentSeasonProducts.length * 0.5)), // 현재 계절 추가
      ...otherSeasonProducts,
      ...yearRoundProducts
    ];
  };

  const searchQueries = getAllSearchQueries();

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // 여러 상품을 한번에 검색
        const randomQuery = searchQueries[Math.floor(Math.random() * searchQueries.length)];
        const response = await fetch(`/api/search-products?query=${encodeURIComponent(randomQuery)}`);
        const data = await response.json();
        
        if (data.products) {
          setSeasonalProducts(data.products);
          
          // Gemini 분석 요청
          const analysisResponse = await fetch('/api/analyze-products', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              query: randomQuery,
              products: data.products
            })
          });
          
          const analysisData = await analysisResponse.json();
          if (analysisData.analysis) {
            setAnalysis(analysisData.analysis);
          }
        }
      } catch (error) {
        console.error('상품 로딩 오류:', error);
        // 오류 시 기본 데이터 사용
        setSeasonalProducts([
          { id: 1, name: '강원도 감자', price: '15,000원', unit: '5kg', badge: '이달의 BEST', image: '🥔', discount: '-20%' },
          { id: 2, name: '평창 고랭지 배추', price: '8,000원', unit: '1포기', badge: '신선도 1위', image: '🥬', discount: '-15%' },
          { id: 3, name: '홍천 찰옥수수', price: '12,000원', unit: '10개입', badge: '재구매율 1위', image: '🌽', discount: null },
          { id: 4, name: '양구 시래기', price: '18,000원', unit: '1kg', badge: '프리미엄', image: '🥗', discount: '-10%' },
          { id: 5, name: '횡성 한우', price: '89,000원', unit: '600g', badge: '명품인증', image: '🥩', discount: null },
          { id: 6, name: '속초 오징어', price: '25,000원', unit: '5마리', badge: '당일배송', image: '🦑', discount: '-25%' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-blue-50"
        data-onboarding="hero"
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full opacity-20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 px-4 py-1.5 bg-green-100 text-green-700 border-green-200">
              <Sparkles className="w-3 h-3 mr-1" />
              강원도 제철 특산물 플랫폼
            </Badge>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              신선한 강원도를<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">
                집에서 만나보세요
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              농가 직송 제철 특산물과 AI가 추천하는 맞춤 레시피로
              건강하고 맛있는 식탁을 완성하세요
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/seasonal">
                <Button size="lg" className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
                  <Leaf className="w-5 h-5 mr-2" />
                  제철 특산물 보기
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link href="/recipes">
                <Button size="lg" variant="outline" className="px-8 py-6 text-lg rounded-xl border-2 hover:bg-gray-50">
                  <ChefHat className="w-5 h-5 mr-2" />
                  AI 레시피 탐색
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
              <div className="flex items-center gap-2 text-gray-600">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">농가 직거래 인증</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Award className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium">품질 보증 시스템</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium">10,000+ 만족 고객</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gangwon Map Section */}
      <section className="pt-1 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Badge className="mb-1 bg-blue-100 text-blue-700 border-blue-200">
              <Map className="w-3 h-3 mr-1" />
              지역별 특산품
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
              강원도 지역별 특산품 지도
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-2">
              각 지역을 클릭하여 특별한 특산품들을 만나보세요
            </p>
          </div>
          <GangwonMap />
        </div>
      </section>

      {/* Seasonal Best Products */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-100 text-orange-700 border-orange-200">
              HOT
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              이달의 제철 특산물 BEST
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              지금 가장 신선하고 맛있는 강원도 특산물을 만나보세요
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="border-gray-100 animate-pulse">
                  <CardContent className="p-4">
                    <div className="bg-gray-200 h-16 rounded mb-3"></div>
                    <div className="bg-gray-200 h-4 rounded mb-2"></div>
                    <div className="bg-gray-200 h-3 rounded mb-1"></div>
                    <div className="bg-gray-200 h-3 rounded mb-2"></div>
                    <div className="bg-gray-200 h-5 rounded"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {seasonalProducts.map((product) => (
                  <div key={product.id}>
                    <Link href={product.link || `/products/${product.name}`} target={product.link ? "_blank" : "_self"}>
                      <Card className="group hover:shadow-lg transition-all cursor-pointer border-gray-100 hover:border-green-200">
                        <CardContent className="p-4">
                        <div className="relative mb-3">
                          <div className="text-6xl text-center mb-2">{product.image}</div>
                          {product.discount && (
                            <Badge className="absolute top-0 right-0 bg-red-500 text-white border-0">
                              {product.discount}
                            </Badge>
                          )}
                        </div>
                        <Badge variant="secondary" className="mb-2 text-xs w-full justify-center">
                          {product.badge}
                        </Badge>
                        <h3 className="font-semibold text-sm mb-1">{product.name}</h3>
                        <p className="text-xs text-gray-500 mb-2">{product.unit}</p>
                        <p className="font-bold text-lg text-green-600">{product.price}</p>
                        {product.mall && (
                          <p className="text-xs text-gray-400 mt-1">{product.mall}</p>
                        )}
                        </CardContent>
                      </Card>
                    </Link>
                    <ProductAnalysis product={product} size="sm" />
                  </div>
                ))}
              </div>
              
              {/* Gemini 분석 결과 */}
              {analysis && (
                <div className="mt-8">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
                        <Sparkles className="w-5 h-5" />
                        AI 가격 분석 결과
                      </h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowAnalysis(!showAnalysis)}
                      >
                        {showAnalysis ? '숨기기' : '분석 보기'}
                      </Button>
                    </div>
                    {showAnalysis && (
                      <div className="text-blue-700 text-sm whitespace-pre-line leading-relaxed">
                        {analysis}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="text-center mt-8">
            <Button variant="outline" size="lg" className="rounded-full" asChild>
              <Link href="/seasonal">
                더 많은 제철 특산물 보기
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Trending Recipes Carousel */}
      <section className="py-2 md:py-4 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-2">
            <Badge className="mb-2 bg-purple-100 text-purple-700 border-purple-200">
              <TrendingUp className="w-3 h-3 mr-1" />
              TRENDING
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              인기 급상승 레시피
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              AI가 추천하는 제철 재료 활용 레시피를 확인해보세요
            </p>
          </div>

          <TrendingRecipeCarousel recipes={trendingRecipes} />
        </div>
      </section>

      {/* Farm Stories Preview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-blue-200">
              STORY
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              농가 이야기
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              정직하게 재배하는 강원도 농가들의 진짜 이야기를 들어보세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {farmStories.map((story) => (
              <Card key={story.id} className="hover:shadow-lg transition-all cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg mb-1">{story.farm}</CardTitle>
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-3 h-3 mr-1" />
                        {story.location}
                      </div>
                    </div>
                    {story.verified && (
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        인증
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-3">{story.title}</p>
                  <div className="flex items-center">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-semibold ml-1">{story.rating}</span>
                    </div>
                    <span className="text-sm text-gray-500 ml-2">신뢰도 평점</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link href="/farmers">
              <Button variant="outline" size="lg" className="rounded-full">
                더 많은 농가 스토리 보기
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            지금 바로 시작하세요
          </h2>
          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">
            신선한 강원도 제철 특산물과 AI 레시피로 건강한 식탁을 만들어보세요
          </p>
          <Link href="/seasonal">
            <Button size="lg" className="bg-white text-green-600 hover:bg-gray-50 px-8 py-6 text-lg rounded-xl shadow-lg">
              제철 특산물 둘러보기
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}